import {
	createFileRoute,
	Link,
	redirect,
	stripSearchParams,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Fragment, useEffect, useMemo, useTransition } from "react";
import {
	fetchQuery,
	graphql,
	useFragment,
	useLazyLoadQuery,
	usePaginationFragment,
} from "react-relay";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { environment } from "@/relay/environment";
import { CreateEventButton } from "@/ui/create-event-button";
import { EventCard } from "@/ui/event-card";
import { Portals } from "@/ui/portals";
import { SidebarPreview } from "@/ui/sidebar-preview";
import { cn } from "@/utils/cn";
import type { eventosEnabledQuery } from "./__generated__/eventosEnabledQuery.graphql";
import type { eventosFeedConnectionQuery } from "./__generated__/eventosFeedConnectionQuery.graphql";
import type { eventosFeedFragment$key } from "./__generated__/eventosFeedFragment.graphql";
import type {
	eventosMonthTimeline_events$data,
	eventosMonthTimeline_events$key,
} from "./__generated__/eventosMonthTimeline_events.graphql";
import type { eventosQuery } from "./__generated__/eventosQuery.graphql";

export const defaultValues = {
	filter: "upcoming",
} as const;

export const Route = createFileRoute("/_visibility/_topnav/_tabs/eventos")({
	component: RouteComponent,
	validateSearch: z.object({
		filter: z.enum(["upcoming", "past"]).default("upcoming"),
	}),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
	pendingComponent: () => {
		return (
			<div className="size-full flex flex-col items-center justify-center py-12">
				<Spinner />
			</div>
		);
	},
	pendingMinMs: 300,
	loader: async ({ context }) => {
		const { club } = context;

		const data = await fetchQuery<eventosEnabledQuery>(
			environment,
			graphql`
		    query eventosEnabledQuery($slug: String!) {
		      club(slug: $slug) {
				    configuration(key: "EVENTS_ENABLED")
			    }
		    }
	`,
			{ slug: club.slug },
		).toPromise();

		if (data?.club.configuration?.length && data?.club.configuration !== "1") {
			throw redirect({
				to: "/",
			});
		}
	},
});

function EventMonthTimeline({
	month,
}: {
	month: eventosMonthTimeline_events$key;
}) {
	const { filter } = useSearch({ from: Route.id });
	const data = useFragment<eventosMonthTimeline_events$key>(
		graphql`
    fragment eventosMonthTimeline_events on MonthEvents {
        events {
          id
          startDate
          ...eventCard
        }
    }
  `,
		month,
	);

	const eventsByDays = useMemo(() => {
		return data.events.reduce<
			Record<string, eventosMonthTimeline_events$data["events"][number][]>
		>((acc, d) => {
			const date = format(new Date(d.startDate), "yyyy-MM-dd");

			if (acc[date]) {
				acc[date].push(d);
			} else {
				acc[date] = [d];
			}

			return acc;
		}, {});
	}, [data.events]);

	const eventDates = useMemo(() => {
		if (filter === "upcoming") {
			return Object.keys(eventsByDays).sort();
		}

		return Object.keys(eventsByDays).sort().reverse();
	}, [eventsByDays, filter]);

	return (
		<>
			{eventDates.map((s, index) => (
				<Fragment key={s}>
					<div
						key={s}
						className={cn(
							"flex flex-row items-center gap-6",
							index !== 0 && "mt-6",
						)}
					>
						<div className="min-w-8 flex items-center justify-end">
							<span className="text-2xl text-primary font-semibold text-right">
								{format(parse(s, "yyyy-MM-dd", new Date()), "dd")}
							</span>
						</div>

						<span className="uppercase text-sm text-muted-foreground">
							{
								format(parse(s, "yyyy-MM-dd", new Date()), "EEEE", {
									locale: ptBR,
								}).split("-")[0]
							}
						</span>

						<Separator className="bg-muted-foreground flex-1" />
					</div>

					{eventsByDays[s].map((ev) => (
						<EventCard event={ev} key={ev.id} />
					))}
				</Fragment>
			))}
		</>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { filter: filterParam } = useSearch({ from: Route.id });

	const [isPending, startTransition] = useTransition();

	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const filter = filterParam === "upcoming" ? "UPCOMING" : "PAST";
	const orderBy = filter === "UPCOMING" ? "CHRONOLOGICAL" : "REVERSE";

	const data = useLazyLoadQuery<eventosQuery>(
		graphql`
        query eventosQuery($slug: String!, $orderBy: EventFeedOrdering!, $filter: EventFilter!) {
          club(slug: $slug) {
            id
            permissions {
                slug
            }

            leaderboard: configuration(key: "LEADERBOARD")
            ranking {
              rankingUsers: users(first: 5) {
                edges {
                  node {
                    totalPoints
                  }
                }
              }
            }

            ...portals_club
            ...sidebarPreview @arguments(orderBy: TOTAL_POINTS_CURRENT_MONTH)
            ...eventosFeedFragment @arguments(orderBy: $orderBy, filter: $filter)
          }
          user {
            ...portals_user @arguments(club: $slug)
          }
        }
    `,
		{
			slug: club?.slug || "",
			orderBy: orderBy,
			filter: filter,
		},
	);

	const {
		data: { eventsByMonth },
		loadNext,
	} = usePaginationFragment<
		eventosFeedConnectionQuery,
		eventosFeedFragment$key
	>(
		graphql`
	    fragment eventosFeedFragment on Club
			@refetchable(queryName: "eventosFeedConnectionQuery")
			@argumentDefinitions(
			    orderBy: { type: "EventFeedOrdering!"},
					filter: { type: "EventFilter" }
					first: { type: "Int", defaultValue: 5 },
					cursor: { type: "String" }
			)
			{
				eventsByMonth(orderBy: $orderBy, filter: $filter, first: $first, after: $cursor)
				@connection(key: "eventosFeedFragment__eventsByMonth")
			{
          edges {
            node {
              title
              ...eventosMonthTimeline_events
            }
          }
          pageInfo {
            hasNextPage
          }
        }
			}
	`,
		data.club,
	);

	useEffect(() => {
		if (
			!isPending &&
			entry?.isIntersecting &&
			eventsByMonth.pageInfo.hasNextPage
		) {
			startTransition(() => {
				loadNext(10);
			});
		}
	}, [
		entry?.isIntersecting,
		isPending,
		loadNext,
		eventsByMonth.pageInfo.hasNextPage,
	]);

	const hasLeaderboardUsers =
		data.club.ranking?.rankingUsers.edges.filter(
			(ed) => ed.node.totalPoints > 0,
		).length > 0;
	const isLeaderboardEnabled =
		data.club.leaderboard === "ENABLED" || data.club.leaderboard === "1";
	const isSidebarHidden = !isLeaderboardEnabled || !hasLeaderboardUsers;
	const shouldReduceWidth = !eventsByMonth.edges?.length && isSidebarHidden;

	return (
		<div className="flex flex-col justify-center items-center mt-6 w-full px-6">
			<div className="w-full lg:max-w-[1000px] empty:hidden not-empty:mb-6">
				<Portals club={data.club} user={data.user} />
			</div>
			<div className="lg:max-w-[1000px] gap-6 pb-6 w-full flex flex-row justify-between items-start overflow-x-hidden">
				<div
					className={cn(
						"flex flex-col flex-1 w-full gap-6 lg:last:w-full max-lg:w-full",
						!shouldReduceWidth &&
							"lg:not-last:max-w-[calc(1000px-340px-1.5rem)]",
						shouldReduceWidth && "lg:not-last:max-w-[600px]",
					)}
				>
					<CreateEventButton />

					<Card className="p-6 flex flex-row gap-3 items-center justify-between w-full">
						<div className="flex flex-row gap-3">
							<Link
								from={Route.fullPath}
								search={(s) => ({ ...s, filter: "upcoming" })}
							>
								<Button variant={filter === "UPCOMING" ? "default" : "ghost"}>
									Próximos
								</Button>
							</Link>

							<Link
								from={Route.fullPath}
								search={(s) => ({ ...s, filter: "past" })}
							>
								<Button variant={filter === "PAST" ? "default" : "ghost"}>
									Anteriores
								</Button>
							</Link>
						</div>
					</Card>

					{eventsByMonth.edges?.filter(Boolean).map((ev, index) => (
						<div
							key={ev.node.title}
							className={cn("flex flex-col gap-3 mt-6", index === 0 && "mt-0")}
						>
							<span className="font-semibold text-lg">
								<span className="capitalize">
									{format(parse(ev.node.title, "MM/yyyy", new Date()), "MMMM", {
										locale: ptBR,
									})}
								</span>{" "}
								de{" "}
								<span className="capitalize">
									{format(parse(ev.node.title, "MM/yyyy", new Date()), "yyyy", {
										locale: ptBR,
									})}
								</span>
							</span>

							<EventMonthTimeline month={ev.node} />
						</div>
					))}

					<div ref={ref} className="size-1" />

					{isPending && (
						<div className="flex items-center justify-center">
							<Spinner />
						</div>
					)}
				</div>

				<SidebarPreview club={data.club} />
			</div>
		</div>
	);
}
